import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard,
    Receipt,
    AlertCircle,
    FileText,
    Users,
    LogOut,
    Menu,
    X,
    ChevronRight,
    FileCheck,
    List,
    Package,
    Barcode,
    UserSquare,
    BookmarkCheck,
    Tag,
    FolderTree,
    Truck,
    Boxes,
    CalendarClock,
    Mail,
    BookOpen,
    Settings as SettingsIcon,
    Cloud,
} from 'lucide-react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { BRAND_ASSETS } from '../config/brandAssets';
import { PORTAL_LINK } from '../config/appMode';
import { useSystemHealth } from '../hooks/useSystemHealth';

const LOGO_URL = BRAND_ASSETS.LOGO_PRIMARY;


/**
 * Sidebar navigation link.
 *
 * Hoisted out of `Layout` (Wave 3) so React sees a stable component
 * type across renders — otherwise every parent state change would
 * destroy and recreate every `<NavLink>` in the sidebar, wiping their
 * DOM nodes and any internal state.
 */
const NavLink = ({ item, mobile = false, activePath, sidebarOpen, closeMobile, t }) => {
    const isActive = activePath === item.path || (item.path === '/sales' && activePath.startsWith('/sales'));
    const Icon = item.icon;
    return (
        <Link
            to={item.path}
            data-testid={`nav-${item.path.replace('/', '')}`}
            onClick={() => mobile && closeMobile()}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                    ? 'bg-[#C08478]/20 text-[#C08478] border-l-3 border-[#C08478]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
        >
            <span className="relative inline-flex">
                <Icon size={20} />
                {item.alert && (
                    <span
                        data-testid={`nav-alert-${item.path.replace('/', '')}`}
                        role="status"
                        aria-label={t('nav:health.alert_aria', { defaultValue: 'System health probe failing — open Settings' })}
                        title={t('nav:health.alert_tooltip', { defaultValue: 'A system-health probe is failing — open Settings to inspect.' })}
                        className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-[#1A1A1A]"
                    />
                )}
            </span>
            {(sidebarOpen || mobile) && <span className="font-medium">{item.label}</span>}
        </Link>
    );
};

const SectionCaption = ({ label, mobile = false, sidebarOpen }) => {
    if (!sidebarOpen && !mobile) return null;
    return (
        <div className="pt-4 pb-1 first:pt-2">
            <span className="px-4 text-[10px] font-semibold text-gray-500 uppercase tracking-[1.5px]">
                {label}
            </span>
        </div>
    );
};

export const Layout = ({ children }) => {
    const { user, logout, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(['nav', 'common']);
    const lang = (i18n.resolvedLanguage || i18n.language || 'fr').slice(0, 2);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Passive downstream-health poll — admin only. When any probe reports
    // ok=false we surface a red dot on the sidebar Settings icon so the
    // admin sees a passive alert without visiting the diagnostic page.
    const { hasIssue: healthHasIssue } = useSystemHealth();

    // Sectioned sidebar — items are grouped by domain so the menu stays
    // scannable. Each section renders an UPPERCASE caption (when the sidebar
    // is expanded) followed by its NavLinks.
    const navSections = useMemo(() => ([
        {
            id: 'overview',
            label: t('nav:sections.overview', { defaultValue: 'Tableau de Bord' }),
            items: [
                { path: '/dashboard', label: t('nav:items.dashboard'), icon: LayoutDashboard },
            ],
        },
        {
            id: 'crm',
            label: t('nav:sections.crm', { defaultValue: 'CRM' }),
            items: [
                { path: '/customers', label: t('nav:items.customers'), icon: UserSquare },
            ],
        },
        {
            id: 'sales',
            label: t('nav:sections.sales', { defaultValue: 'Ventes' }),
            items: [
                { path: '/sales', label: t('nav:items.sales'), icon: Receipt },
                { path: '/drafts', label: t('nav:items.drafts_quotations'), icon: BookmarkCheck },
                { path: '/cloud-orders', label: t('nav:items.cloud_orders', { defaultValue: 'Cloud orders' }), icon: Cloud },
                { path: '/portal-customers', label: t('nav:items.portal_customers', { defaultValue: 'Portal customers' }), icon: UserSquare },
                { path: '/transactions', label: t('nav:items.transactions'), icon: List },
                { path: '/dues', label: t('nav:items.due_payments'), icon: AlertCircle },
                { path: '/cheques', label: t('nav:items.cheques'), icon: FileCheck },
            ],
        },
        {
            id: 'inventory',
            label: t('nav:sections.inventory', { defaultValue: 'Inventaire' }),
            items: [
                { path: '/products', label: t('nav:items.products'), icon: Package },
                { path: '/products/barcode-assignment', label: t('nav:items.barcode_assignment', { defaultValue: 'Barcode assignment' }), icon: Barcode },
                { path: '/categories', label: t('nav:items.categories'), icon: FolderTree },
                { path: '/brands', label: t('nav:items.brands'), icon: Tag },
            ],
        },
        {
            id: 'supply_chain',
            label: t('nav:sections.supply_chain', { defaultValue: "Chaîne d'approvisionnement" }),
            items: [
                { path: '/suppliers', label: t('nav:items.suppliers'), icon: Truck },
            ],
        },
        {
            id: 'stock_control',
            label: t('nav:sections.stock_control', { defaultValue: 'Contrôle des stocks' }),
            items: [
                { path: '/batches', label: t('nav:items.batches'), icon: Boxes },
                { path: '/expiry', label: t('nav:items.expiry'), icon: CalendarClock },
            ],
        },
        {
            id: 'reports',
            label: t('nav:sections.reports_docs', { defaultValue: 'Rapports & Documents' }),
            items: [
                { path: '/reports', label: t('nav:items.reports'), icon: FileText },
                { path: '/catalog', label: t('nav:items.catalog', { defaultValue: 'Catalogue' }), icon: BookOpen },
                { path: '/letter', label: t('nav:items.letter', { defaultValue: 'Letterhead' }), icon: Mail },
            ],
        },
    ]), [t]);

    const adminItems = useMemo(() => ([
        { path: '/users', label: t('nav:items.users'), icon: Users },
        {
            path: '/settings',
            label: t('nav:items.settings', { defaultValue: 'Settings' }),
            icon: SettingsIcon,
            alert: healthHasIssue,   // red dot on the icon when any /health/deep probe is red
        },
    ]), [t, healthHasIssue]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const dateLocale = lang === 'fr' ? 'fr-FR' : 'en-GB';

    const formatDate = () =>
        currentTime.toLocaleDateString(dateLocale, {
            weekday: 'long',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });

    const formatTime = () =>
        currentTime.toLocaleTimeString(dateLocale, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

    // Shared props for the hoisted NavLink / SectionCaption components.
    const navBinding = {
        activePath: location.pathname,
        sidebarOpen,
        closeMobile: () => setMobileOpen(false),
        t,
    };
    const captionBinding = { sidebarOpen };


    return (
        <div className="flex min-h-screen bg-[#FAFAFA]">
            {/* Desktop Sidebar */}
            <aside
                className={`hidden md:flex flex-col bg-[#1A1A1A] text-white transition-all duration-300 no-print ${
                    sidebarOpen ? 'w-64' : 'w-20'
                }`}
            >
                <div className="p-4 border-b border-white/10">
                    <div className={`flex items-start ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
                        <img
                            src={LOGO_URL}
                            alt="UNIVERS COSMETIC"
                            className={`object-contain rounded-lg bg-white p-1 ${sidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}
                        />
                        {sidebarOpen && (
                            <div className="flex-1">
                                <h1 className="text-sm tracking-tight">
                                    <span className="font-bold">UNIVERS</span>{' '}
                                    <span className="font-normal">COSMETIC</span>
                                </h1>
                                <p className="text-xs text-green-400 font-medium mt-0.5">{formatDate()}</p>
                                <p className="text-sm text-red-500 font-mono font-bold tracking-wider">
                                    {formatTime()}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {navSections.map((section) => (
                        <div key={section.id} className="space-y-1">
                            <SectionCaption label={section.label} {...captionBinding} />
                            {section.items.map((item) => (
                                <NavLink key={item.path} item={item} {...navBinding} />
                            ))}
                        </div>
                    ))}

                    {isAdmin() && (
                        <div className="space-y-1">
                            <SectionCaption label={t('common:common.admin')} {...captionBinding} />
                            {adminItems.map((item) => (
                                <NavLink key={item.path} item={item} {...navBinding} />
                            ))}
                        </div>
                    )}
                </nav>

                <div className="p-4 border-t border-white/10">
                    {sidebarOpen ? (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-[#C08478]/20 flex items-center justify-center">
                                    <span className="text-[#C08478] font-semibold text-sm">
                                        {user?.full_name?.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm font-medium">{user?.full_name}</p>
                                    <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleLogout}
                                data-testid="logout-btn"
                                className="text-gray-400 hover:text-white hover:bg-white/10"
                            >
                                <LogOut size={18} />
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleLogout}
                            className="w-full text-gray-400 hover:text-white hover:bg-white/10"
                        >
                            <LogOut size={18} />
                        </Button>
                    )}
                </div>

                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="absolute top-6 -right-3 w-6 h-6 bg-[#1A1A1A] border border-white/20 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                    <ChevronRight
                        size={14}
                        className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`}
                    />
                </button>
            </aside>

            {/* Mobile Header */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#1A1A1A] text-white p-4 flex items-center justify-between no-print">
                <div className="flex items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt="UNIVERS COSMETIC"
                        className="w-8 h-8 object-contain rounded-lg bg-white p-0.5"
                    />
                    <div>
                        <span className="font-bold text-sm">UNIVERS COSMETIC</span>
                        <p className="text-xs text-green-400">{formatDate()}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <LanguageSwitcher compact />
                    <a
                        href={PORTAL_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="open-portal-btn-mobile"
                        title={t('nav:open_portal', { defaultValue: 'Open B2B Portal' })}
                        className="w-9 h-9 rounded-full bg-[#C08478]/15 hover:bg-[#C08478]/30 border border-[#C08478]/40 flex items-center justify-center text-[#C08478] transition-colors"
                        aria-label="Open B2B portal"
                    >
                        <Cloud size={18} />
                    </a>
                    <NotificationBell />
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        className="text-white"
                    >
                        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                    </Button>
                </div>
            </div>

            {mobileOpen && (
                <div
                    className="md:hidden fixed inset-0 z-40 bg-black/50 no-print"
                    onClick={() => setMobileOpen(false)}
                >
                    <aside
                        className="absolute top-16 left-0 bottom-0 w-64 bg-[#1A1A1A] p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-4 pb-4 border-b border-white/10">
                            <p className="text-lg text-red-500 font-mono font-bold tracking-wider">
                                {formatTime()}
                            </p>
                        </div>

                        <nav className="space-y-1 overflow-y-auto" style={{maxHeight: 'calc(100vh - 200px)'}}>
                            {navSections.map((section) => (
                                <div key={section.id} className="space-y-1">
                                    <SectionCaption label={section.label} mobile {...captionBinding} />
                                    {section.items.map((item) => (
                                        <NavLink key={item.path} item={item} mobile {...navBinding} />
                                    ))}
                                </div>
                            ))}
                            {isAdmin() && (
                                <div className="space-y-1">
                                    <SectionCaption label={t('common:common.admin')} mobile {...captionBinding} />
                                    {adminItems.map((item) => (
                                        <NavLink key={item.path} item={item} mobile {...navBinding} />
                                    ))}
                                </div>
                            )}
                        </nav>
                        <div className="absolute bottom-4 left-4 right-4">
                            <Button
                                onClick={handleLogout}
                                variant="outline"
                                className="w-full border-white/20 text-white hover:bg-white/10"
                            >
                                <LogOut size={18} className="mr-2" />
                                {t('common:actions.logout')}
                            </Button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Main Content */}
            <main className="flex-1 md:ml-0 mt-16 md:mt-0 min-w-0 overflow-x-hidden">
                <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto">
                    {/* Top utility bar (desktop only) */}
                    <div className="hidden md:flex justify-end items-center gap-3 mb-4 no-print">
                        <LanguageSwitcher />
                        <a
                            href={PORTAL_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="open-portal-btn"
                            title={t('nav:open_portal', { defaultValue: 'Open B2B Portal' })}
                            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#C08478]/40 text-[#C08478] hover:bg-[#C08478] hover:text-white hover:border-[#C08478] text-xs font-semibold uppercase tracking-[2px] transition-colors"
                            aria-label="Open B2B portal"
                        >
                            <Cloud size={14} />
                            <span className="hidden lg:inline">{t('nav:open_portal', { defaultValue: 'B2B Portal' })}</span>
                        </a>
                        <NotificationBell />
                    </div>
                    {children}
                </div>
            </main>
        </div>
    );
};
